import debounce from 'lodash/debounce'
import get from 'lodash/get'
import * as React from 'react'
import type { DropdownOption } from 'types/ui'
import Row from 'components/common/row'
import TagSelector from 'components/common/tag-selector'
import TagList from 'components/explorer/pane/tag-list'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Label from 'components/ui/label'
import ContactCard from '../contact-card'
type Props = {
  contact: Record<string, any>
  changeContactValue: (...args: Array<any>) => any
  updateContacts: (...args: Array<any>) => any
  cancelContactsEditing: (...args: Array<any>) => any
}
type State = {
  editMode?: boolean
}

class TagsTab extends React.Component<Props, State> {
  state: State = {
    editMode: false,
  }
  _onTagRemove: (tag: string) => void = (tag: string) => {
    const { contact } = this.props
    if (!contact) return
    const tagsValue = contact
      ? get(contact.pendingChanges, 'tags', contact.tags || [])
      : []
    const tagList = tagsValue.filter(t => t !== tag)
    this.props.changeContactValue(contact.id, 'tags', tagList)

    this._scheduleTagUpdate()
  }
  _onTagsSelect: (selectedTagOptions: Array<DropdownOption>) => void = (
    selectedTagOptions: DropdownOption[],
  ) => {
    const { contact } = this.props
    const tags = selectedTagOptions
      .map(t => `${t.value}`)
      .filter(t => t.trim().toLowerCase() !== 'business')
    this.props.changeContactValue(contact.id, 'tags', tags)
  }
  _scheduleTagUpdate: () => void = debounce(() => this._updateTags(), 1000)
  _saveTags: () => void = () => {
    this._updateTags()

    this._toggleEditMode()
  }
  _updateTags: () => void = () => {
    const { contact } = this.props
    // Use the full-update path (not updateContactsTags) so that reset fields
    // staged by the tags reducer — pros/publishers/acts, entity_details
    // defaults, external_identifiers — actually get shipped to the server.
    // The partial-tag path silently drops them.
    if (contact && contact.pendingChanges && contact.pendingChanges.tags)
      this.props.updateContacts([this.props.contact]).catch(() => {
        // error toast message already handled by updateContacts thunk, this catch to supress error to browser
      })
  }
  _toggleEditMode: () => void = () => {
    this.setState(prevState => ({
      editMode: !prevState.editMode,
    }))
  }
  _onCancel: () => void = () => {
    this.props.cancelContactsEditing()
    this._toggleEditMode()
  }

  render(): React.ReactNode {
    const { contact } = this.props
    const { editMode } = this.state
    const tagsValue = contact
      ? get(contact.pendingChanges, 'tags', contact.tags || [])
      : []
    return editMode ? (
      <ContactCard>
        <Row columnsSize={[12]}>
          <div>
            <Label caption="Tags" />
            <TagSelector
              selectedTags={tagsValue}
              placeholder="Add tags"
              clearable
              allowCreate
              tagsType="contact"
              onChange={this._onTagsSelect}
            />
          </div>
        </Row>
        <ButtonContainer>
          <Button
            appearance="primary"
            caption="Save"
            onClick={this._saveTags}
          />
          <Button caption="Cancel" onClick={this._onCancel} />
        </ButtonContainer>
      </ContactCard>
    ) : (
      <ContactCard
        action={{
          content: 'Edit',
          onClick: this._toggleEditMode,
        }}
      >
        {tagsValue.length === 0 ? (
          'Add tags'
        ) : (
          <TagList
            tags={tagsValue}
            tagType="contact"
            onTagRemove={this._onTagRemove}
          />
        )}
      </ContactCard>
    )
  }
}

export default TagsTab
